/**
 * 한국어 주소 → 영문 주소.
 *
 * 우체국/도로명주소 영문 표기 방식을 따른다. 번지가 앞으로 오고 행정 단위가
 * 큰 쪽으로 가면서 쉼표로 이어진다.
 *   "서울특별시 관악구 신림동 1628-31" → "1628-31, Sillim-dong, Gwanak-gu, Seoul"
 *   "서울특별시 관악구 신원로1길 27-11" → "27-11, Sinwon-ro 1-gil, Gwanak-gu, Seoul"
 *
 * 로마자 표기법 제3장 제5항에 따라 행정 구역 단위 앞에는 붙임표를 넣고,
 * **붙임표 앞뒤에서 일어나는 음운 변화는 표기에 반영하지 않는다.**
 * 그래서 접미사를 떼어 낸 어간만 romanize 에 넘긴다.
 */
import { romanizeWord } from './romanize';

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// 광역 단위는 관례 표기가 굳어 있어 변환하지 않고 표를 쓴다.
const WIDE_AREA = {
    '서울특별시': 'Seoul', '서울시': 'Seoul', '서울': 'Seoul',
    '부산광역시': 'Busan', '부산시': 'Busan', '부산': 'Busan',
    '대구광역시': 'Daegu', '대구시': 'Daegu', '대구': 'Daegu',
    '인천광역시': 'Incheon', '인천시': 'Incheon', '인천': 'Incheon',
    '광주광역시': 'Gwangju', '광주시': 'Gwangju',
    '대전광역시': 'Daejeon', '대전시': 'Daejeon', '대전': 'Daejeon',
    '울산광역시': 'Ulsan', '울산시': 'Ulsan', '울산': 'Ulsan',
    '세종특별자치시': 'Sejong', '세종시': 'Sejong', '세종': 'Sejong',
    '경기도': 'Gyeonggi-do',
    '강원도': 'Gangwon-do', '강원특별자치도': 'Gangwon-do',
    '충청북도': 'Chungcheongbuk-do', '충북': 'Chungcheongbuk-do',
    '충청남도': 'Chungcheongnam-do', '충남': 'Chungcheongnam-do',
    '전라북도': 'Jeollabuk-do', '전북특별자치도': 'Jeollabuk-do', '전북': 'Jeollabuk-do',
    '전라남도': 'Jeollanam-do', '전남': 'Jeollanam-do',
    '경상북도': 'Gyeongsangbuk-do', '경북': 'Gyeongsangbuk-do',
    '경상남도': 'Gyeongsangnam-do', '경남': 'Gyeongsangnam-do',
    '제주특별자치도': 'Jeju-do', '제주도': 'Jeju-do',
};

// 긴 접미사를 먼저 봐야 한다 (특별시 < 시, 대로 < 로).
const UNIT_SUFFIXES = [
    ['특별자치시', 'si'], ['특별자치도', 'do'], ['특별시', 'si'], ['광역시', 'si'],
    ['대로', 'daero'], ['시', 'si'], ['군', 'gun'], ['구', 'gu'],
    ['읍', 'eup'], ['면', 'myeon'], ['리', 'ri'], ['동', 'dong'],
    ['가', 'ga'], ['로', 'ro'], ['길', 'gil'], ['도', 'do'],
];

const isNumberToken = (t) => /^[0-9]+(-[0-9]+)?(번지)?$/.test(t);

/** "신원로1길" 처럼 숫자가 끼어 있는 도로명을 "Sinwon-ro 1-gil" 로. */
function romanizeToken(token) {
    if (WIDE_AREA[token]) return WIDE_AREA[token];

    // <이름><숫자><길|가>  예: 신원로1길, 테헤란로12길
    const numbered = /^(.+?)([0-9]+)(길|가)$/.exec(token);
    if (numbered) {
        const [, stem, num, unit] = numbered;
        const unitRoman = unit === '길' ? 'gil' : 'ga';
        return `${romanizeToken(stem)} ${num}-${unitRoman}`;
    }

    for (const [suffix, roman] of UNIT_SUFFIXES) {
        if (token.length > suffix.length && token.endsWith(suffix)) {
            const stem = token.slice(0, -suffix.length);
            if (WIDE_AREA[token]) return WIDE_AREA[token];
            return `${capitalize(romanizeWord(stem))}-${roman}`;
        }
    }
    return capitalize(romanizeWord(token));
}

/**
 * 한국어 주소 문자열을 영문으로 바꾼다.
 * 한글이 없으면(이미 영문이면) 원문을 그대로 돌려준다.
 */
export function romanizeAddress(address) {
    if (!address || typeof address !== 'string') return address;
    if (!/[가-힣]/.test(address)) return address;

    const tokens = address.trim().split(/\s+/).filter(Boolean);
    if (!tokens.length) return address;

    // 뒤쪽에 붙은 번지/건물번호는 영문 주소에서 맨 앞으로 간다.
    const numbers = [];
    while (tokens.length && isNumberToken(tokens[tokens.length - 1])) {
        numbers.unshift(tokens.pop().replace('번지', ''));
    }

    const areas = tokens.map(romanizeToken).reverse();
    const head = numbers.join(' ');
    return [head, ...areas].filter(Boolean).join(', ');
}

export default romanizeAddress;
